import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    Renderer2,
    AfterViewInit,
    OnDestroy,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { ColorModel } from '../../models/color.model';
import { ColorPickerSourceType } from '../../enums/color-picker-source-types.enum';
import { ActionKeys } from '../../enums/keys.enum';

const HSB_STEP = 1;

interface ColorChangeEvent {
    value: ColorModel;
    source: ColorPickerSourceType;
}

/**
 * A focused 2D color gradient picker component with hue slider.
 * This component only handles the visual color selection controls.
 */
@Component({
    selector: 'agpr-color-gradient-picker',
    templateUrl: './color-gradient-picker.component.html',
    styleUrls: ['./color-gradient-picker.component.scss'],
    standalone: false
})
export class ColorGradientPickerComponent implements AfterViewInit, OnDestroy, OnChanges {
    @Input() id: string = '_colorPickerPopover_gradient';
    @Input() value: ColorModel;
    @Input() disabled: boolean = false;

    @Output() colorChange = new EventEmitter<ColorChangeEvent>();

    @ViewChild('colorSelector') colorSelectorRef: ElementRef;
    @ViewChild('colorHandle') colorHandleRef: ElementRef;
    @ViewChild('hueSlider') hueSliderRef: ElementRef;
    @ViewChild('hueHandle') hueHandleRef: ElementRef;

    colorDragging = false;
    hueDragging = false;

    private documentMousemoveListener: () => void;
    private documentMouseupListener: () => void;

    constructor(private renderer: Renderer2) {}

    ngAfterViewInit(): void {
        this.updateUI();
        this.updateColorSelectorBackground();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] && !changes['value'].firstChange) {
            this.updateUI();
            this.updateColorSelectorBackground();
        }
    }

    ngOnDestroy(): void {
        this.unbindDocumentListeners();
    }

    onColorMousedown(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        this.bindDocumentListeners();
        this.colorDragging = true;
        this.pickColor(event);
    }

    onHueMousedown(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        this.bindDocumentListeners();
        this.hueDragging = true;
        this.pickHue(event);
    }

    onColorKeydown(event: KeyboardEvent): void {
        if (this.disabled) {
            return;
        }

        let saturation = this.value.hsb.s;
        let brightness = this.value.hsb.b;

        switch (event.key) {
            case ActionKeys.LEFT:
                saturation -= HSB_STEP;
                break;
            case ActionKeys.RIGHT:
                saturation += HSB_STEP;
                break;
            case ActionKeys.UP:
                brightness += HSB_STEP;
                break;
            case ActionKeys.DOWN:
                brightness -= HSB_STEP;
                break;
            default:
                return;
        }
        event.preventDefault();

        this.value.hsb = ColorModel.validateHSB({
            h: this.value.hsb.h,
            s: saturation,
            b: brightness
        });
        this.value.hex = '#' + ColorModel.HSBtoHEX(this.value.hsb);
        this.value.rgb = ColorModel.HSBtoRGB(this.value.hsb);

        this.updateUI();
        this.emitChange(ColorPickerSourceType.GRADIENT);
    }

    onHueKeydown(event: KeyboardEvent): void {
        if (this.disabled) {
            return;
        }

        let hue = this.value.hsb.h;

        switch (event.key) {
            case ActionKeys.UP:
                hue += HSB_STEP;
                break;
            case ActionKeys.DOWN:
                hue -= HSB_STEP;
                break;
            default:
                return;
        }
        event.preventDefault();

        this.value.hsb = ColorModel.validateHSB({
            h: hue,
            s: this.value.hsb.s,
            b: this.value.hsb.b
        });
        this.value.hex = '#' + ColorModel.HSBtoHEX(this.value.hsb);
        this.value.rgb = ColorModel.HSBtoRGB(this.value.hsb);

        this.updateColorSelectorBackground();
        this.updateUI();
        this.emitChange(ColorPickerSourceType.HUE);
    }

    private pickColor(event: MouseEvent): void {
        const rect = this.colorSelectorRef.nativeElement.getBoundingClientRect();
        const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
        const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

        const saturation = Math.round((x / rect.width) * 100);
        const brightness = Math.round(100 - (y / rect.height) * 100);

        this.value.hsb = ColorModel.validateHSB({
            h: this.value.hsb.h,
            s: saturation,
            b: brightness
        });

        this.value.hex = '#' + ColorModel.HSBtoHEX(this.value.hsb);
        this.value.rgb = ColorModel.HSBtoRGB(this.value.hsb);

        this.updateColorHandle(x, y);
    }

    private pickHue(event: MouseEvent): void {
        const rect = this.hueSliderRef.nativeElement.getBoundingClientRect();
        const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

        const hue = Math.round(360 - (y / rect.height) * 360);

        this.value.hsb = ColorModel.validateHSB({
            h: hue,
            s: this.value.hsb.s,
            b: this.value.hsb.b
        });

        this.value.hex = '#' + ColorModel.HSBtoHEX(this.value.hsb);
        this.value.rgb = ColorModel.HSBtoRGB(this.value.hsb);

        this.updateColorSelectorBackground();
        this.updateHueHandle(y);
    }

    private updateUI(): void {
        if (!this.value || !this.colorSelectorRef?.nativeElement || !this.hueSliderRef?.nativeElement) {
            return;
        }

        const colorRect = this.colorSelectorRef.nativeElement.getBoundingClientRect();
        const hueRect = this.hueSliderRef.nativeElement.getBoundingClientRect();

        const colorX = (this.value.hsb.s / 100) * colorRect.width;
        const colorY = ((100 - this.value.hsb.b) / 100) * colorRect.height;
        const hueY = ((360 - this.value.hsb.h) / 360) * hueRect.height;

        this.updateColorHandle(colorX, colorY);
        this.updateHueHandle(hueY);
    }

    private updateColorHandle(x: number, y: number): void {
        if (this.colorHandleRef?.nativeElement) {
            this.colorHandleRef.nativeElement.style.left = `${x}px`;
            this.colorHandleRef.nativeElement.style.top = `${y}px`;
        }
    }

    private updateHueHandle(y: number): void {
        if (this.hueHandleRef?.nativeElement) {
            this.hueHandleRef.nativeElement.style.top = `${y}px`;
        }
    }

    private updateColorSelectorBackground(): void {
        if (!this.value || !this.colorSelectorRef?.nativeElement) {
            return;
        }

        // Set the background color based on the current hue
        const hueColor = ColorModel.HSBtoHEX({
            h: this.value.hsb.h,
            s: 100,
            b: 100
        });

        this.colorSelectorRef.nativeElement.style.backgroundColor = '#' + hueColor;
    }

    private emitChange(source: ColorPickerSourceType): void {
        this.colorChange.emit({value: this.value.clone(), source});
    }

    private bindDocumentListeners(): void {
        if (!this.documentMousemoveListener) {
            this.documentMousemoveListener = this.renderer.listen('document', 'mousemove', (event: MouseEvent) => {
                if (this.colorDragging) {
                    this.pickColor(event);
                }
                if (this.hueDragging) {
                    this.pickHue(event);
                }
            });
        }

        if (!this.documentMouseupListener) {
            this.documentMouseupListener = this.renderer.listen('document', 'mouseup', (event: MouseEvent) => {
                const colorChangeType = this.colorDragging ? ColorPickerSourceType.GRADIENT: ColorPickerSourceType.HUE;
                this.colorDragging = false;
                this.hueDragging = false;
                this.emitChange(colorChangeType);
                this.unbindDocumentListeners();
            });
        }
    }

    private unbindDocumentListeners(): void {
        if (this.documentMousemoveListener) {
            this.documentMousemoveListener();
            this.documentMousemoveListener = null;
        }
        if (this.documentMouseupListener) {
            this.documentMouseupListener();
            this.documentMouseupListener = null;
        }
    }
}
